/* External */
import {
  memo, useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';
import { FormattedMessage } from 'react-intl';
import jwtDecode from 'jwt-decode';

/* Theme Assets */
import Logo from '~/assets/theme/svg/brand-logo.svg';
import Profiles from '~/assets/theme/svg/profiles.svg';

/* Other */
import { getJsonImage } from 'src/theme/images';

/* Components */
import FocusableButtonprofile from '../../../../components/FocusableButtonProfile';

/* Utils */
import { Routes, getLink } from '../../../../utils/router';
import { TYPE_ACTIONS_PROFILE } from '../../../../utils/constants';
import { useCountry } from '../../../../providers/ConfigProvider';
import { useGtmLocalContext } from '../../../../providers/GtmLocalProvider';

/* GTM */
import { GTM_CATEGORIES, GTM_EVENT_NAMES, GTM_SUBCATEGORIES } from '../../../../utils/googleTags';

/* Storage */
import { createProfile } from '../../../../storage/profiles/actions';
import { createProfileAPI } from '../../../../services/profiles';

/* Style */
import './Onboarding.scss';

const decodeOnboarding = (accessTokenData) => {
  const { idToken } = JSON.parse(accessTokenData);

  const { businessUnit } = jwtDecode(idToken);

  const ott = businessUnit.toLowerCase().includes('ott');

  const { name } = jwtDecode(idToken);

  const ottClientName = ott ? name : '';

  return { ottClientName, ott };
};

function Onboarding({ realFocusKey, setFocus }) {
  const onboardingButtonFocusKey = `${realFocusKey}-onboarding-button`;
  const history = useHistory();
  const dispatch = useDispatch();
  const objOtt = decodeOnboarding(localStorage.getItem('accessTokenData'));
  const ottClient = objOtt.ottClientName;
  const country = useCountry();
  const { commonTaggingEvent } = useGtmLocalContext();
  const tagsMountRef = useRef(true);
  const [buttonLoading, setButtonLoading] = useState(false);

  const profileCommonTagEvent = useCallback(({ eventName }) => {
    if (eventName) {
      commonTaggingEvent({
        category: GTM_CATEGORIES.PROFILES_DATA,
        subCategory: GTM_SUBCATEGORIES.PROFILE.PROFILES_ONBOARDING,
        eventName,
      });
    }
  }, [commonTaggingEvent]);

  useEffect(() => {
    setFocus(onboardingButtonFocusKey);
  }, [onboardingButtonFocusKey, setFocus]);

  useEffect(() => {
    if (tagsMountRef.current && country) {
      profileCommonTagEvent({ eventName: GTM_EVENT_NAMES.PAGE_VIEW });
      tagsMountRef.current = false;
    }
  }, [profileCommonTagEvent, country]);

  const handleClick = useCallback(() => {
    setButtonLoading(true);
    profileCommonTagEvent({ eventName: GTM_EVENT_NAMES.PROFILES_ONBOARDING.CONTINUE_ONBOARDING });
    if (ottClient) {
      createProfileAPI({
        name: ottClient,
        avatar: '',
        ageRating: '18',
      }).then((res) => {
        dispatch(createProfile(res.data.profiles));
        localStorage.setItem('profiles', JSON.stringify(res.data.profiles));
        history.push(
          getLink(Routes.Private.Profiles),
          {
            create: true,
            typeCreate: TYPE_ACTIONS_PROFILE.ONBOARDING,
          },
        );
        setButtonLoading(false);
        return true;
      })
        .catch((error) => {
          setButtonLoading(false);
          history.push(getLink(Routes.Private.Profiles.Error), {
            source: 'MIDDLEWARE',
            location: 'PROFILE_CREATE',
            type: 'UNEXPECTED',
            externCode: error?.response?.status,
            typeError: TYPE_ACTIONS_PROFILE.ONBOARDING,
          });
          return false;
        });
    } else {
      setButtonLoading(false);
      history.push(
        getLink(Routes.Private.Profiles.Create.Name),
        { typeCreateName: TYPE_ACTIONS_PROFILE.ONBOARDING },
      );
    }
  }, [dispatch, history, ottClient, profileCommonTagEvent]);

  return (
    <div className="dtv-html5-c-onboarding">
      <img src={getJsonImage('brand-logo') || Logo} alt="dtv-logo" className="dtv-html5-c-onboarding--logo" />
      <div className="dtv-html5-c-onboarding--centralized">
        <div>
          <img src={getJsonImage('profiles') || Profiles} alt="dtv-profiles" />
          <div className="dtv-html5-c-onboarding--title">
            {ottClient === '' ? (
              <div className="dtv-html5-c-onboarding--title-h1">
                <FormattedMessage id="profile.onboarding" />
              </div>
            ) : (
              <div className="dtv-html5-c-onboarding--title-h1">
                <FormattedMessage id="profile.onboarding.ottClient.1" />
                {' '}
                {ottClient}
                !
                <br />
                <FormattedMessage id="profile.onboarding.ottClient.2" />
              </div>
            )}
            <p className="dtv-html5-c-onboarding--title-subtitle">
              <FormattedMessage id="profile.onboarding.subtitle.1" />
              <br />
              <FormattedMessage id="profile.onboarding.subtitle.2" />
            </p>
          </div>
        </div>
      </div>
      <FocusableButtonprofile
        type="submit"
        secondGray
        bordered
        focusKey={onboardingButtonFocusKey}
        className="dtv-html5-c-onboarding--button"
        onEnterPress={handleClick}
        disabled={buttonLoading}
      >
        <FormattedMessage id="common.continue" />
      </FocusableButtonprofile>

    </div>
  );
}

Onboarding.propTypes = {
  realFocusKey: PropTypes.string.isRequired,
  setFocus: PropTypes.func.isRequired,
};

export default memo(withFocusable()(memo(Onboarding)));
